import { ProTable } from '@ant-design/pro-table';
import { trpc } from '@tencent/tpro_rmonitor_metrics_server_get_metrics';
import { TablePaginationConfig, Form, Tabs } from 'antd';
import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { toSearchStr, BASE_NAME } from '@/infrastructure/history';
import { getSortType } from '@/infrastructure/page-sort';
import { getProductId } from '@/infrastructure/product-info';
import { formatByte2Mb } from '@/infrastructure/utils/digit';
import { toArray } from '@/infrastructure/utils/to-array';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { CommonFieldKey } from '@/proto/field';
import { MetricSubType } from '@/proto/metric';
import { genStandardToken, mb2Byte, parseStandardToken } from '@/proto/token';
import { metricService } from '@/service/metric';
import { WithTypeCast } from '@raft/utils';
import { toLineString } from '@raft/utils/src/case/snake-case';
import styles from './index.module.scss';
const { MetricType } = trpc.rmonitor.metrics_server;
type IDimensionData = WithTypeCast<trpc.rmonitor.metrics_server.IDimensionData>;

export const FLOW_OPTIONS = [
  { label: '域名', value: 'domain', key: 'domain' },
  { label: 'URL', value: 'peer_name', key: 'peer_name' },
  { label: '网络库', value: 'traffic_type', key: 'traffic_type' },
  // 暂时隐藏场景
  // { label: '场景', value: 'stage' },
];

type FlowAnalysisData = {
  P50?: number;
  P75?: number;
  P90?: number;
  average?: number;
  total?: number;
  reportCount?: number;
};

const defaultPagination = {
  pageSize: 20,
  current: 1,
  total: 0,
};

export const FlowAnalysis = ({ token }: {token: string}) => {
  const [form] = Form.useForm();
  const [analysisToken, setAnalysisToken] = useState('');
  const [dimensionName, setDimensionName] = useState('domain');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>(defaultPagination);


  useEffect(() => {
    if (token) {
      setToken(token);
    }
  }, [token]);

  const setToken = async (token: string) => {
    const trafficSize = form.getFieldValue('trafficSize');
    const fieldsValue = await parseStandardToken(token);
    const fieldsFilter = fieldsValue.filters ?? [];

    if (trafficSize?.mode === MatchMode.Between) {
      fieldsFilter?.push?.(
        {
          name: CommonFieldKey.TrafficSize,
          values: toArray(trafficSize?.values?.min ? `${mb2Byte(trafficSize?.values?.min)}` : ''),
          operator: 'GE',
        },
        {
          name: CommonFieldKey.TrafficSize,
          values: toArray(trafficSize?.values?.max ? `${mb2Byte(trafficSize?.values?.max)}` : ''),
          operator: 'LE',
        },
      );
    } else if (trafficSize?.values) {
      fieldsFilter?.push?.({
        name: CommonFieldKey.TrafficSize,
        values: toArray(`${mb2Byte(trafficSize.values)}`),
        operator: trafficSize?.mode,
      });
    }
    const newToken = await genStandardToken({
      ...fieldsValue,
      filters: fieldsFilter,
    });
    setAnalysisToken(newToken);
  };

  const columnSortField: Record<string, string> = {
    reportCount: 'count',
    P50: 'traffic_p50',
    P75: 'traffic_p75',
    average: 'traffic_avg',
    total: 'traffic_exception_sum',
  };
  const getIssueUrl = (value: string) => {
    const queryStr = toSearchStr({ token: analysisToken, trafficName: dimensionName, trafficValue: value });
    return `${location.origin}${BASE_NAME}/traffic/issues/detail?${queryStr}`;
  };

  const toDetail = (value: string) => {
    window.open(getIssueUrl(value), '_blank');
  };
  const columns: any = [
    {
      dataIndex: dimensionName,
      title: FLOW_OPTIONS.find(item => item.value === dimensionName)?.label,
      render: (value: any) => <div className={styles.link} onClick={() => toDetail(value)}>{value}</div>,
    },
    {
      dataIndex: 'P50',
      title: 'P50',
      sorter: true,
      render: (_: unknown, record: FlowAnalysisData) => formatByte2Mb(record.P50 ?? 0),
    },
    {
      dataIndex: 'P75',
      title: 'P75',
      sorter: true,
      render: (_: unknown, record: FlowAnalysisData) => formatByte2Mb(record.P75 ?? 0),
    },
    {
      dataIndex: 'P90',
      title: 'P90',
      sorter: true,
      render: (_: unknown, record: FlowAnalysisData) => formatByte2Mb(record.P90 ?? 0),
    },
    {
      dataIndex: 'average',
      title: '平均大小',
      sorter: true,
      render: (_: unknown, record: FlowAnalysisData) => formatByte2Mb(record.average ?? 0),
    },
    {
      dataIndex: 'total',
      title: '总大小',
      sorter: true,
      render: (_: unknown, record: FlowAnalysisData) => formatByte2Mb(record.total ?? 0),
    },
    {
      dataIndex: 'reportCount',
      title: '上报量',
      defaultSortOrder: 'descend',
      sorter: true,
      render: (_: unknown, record: FlowAnalysisData) => record.reportCount,
    },
  ];

  const [soruceData, setSourceData] = useState<IDimensionData | null>(null);

  const genDimensonData = useCallback((dimensonData: IDimensionData | null) => {
    if (!dimensonData) return [];
    const formatData: any = [];
    const records = dimensonData.records || [];
    records.forEach((item) => {
      formatData.push({
        [columns[0].dataIndex]: decodeURIComponent(item.dimensionValue ?? ''),
        [columns[1].dataIndex]: item.metricValues?.[2],
        [columns[2].dataIndex]: item.metricValues?.[3],
        [columns[3].dataIndex]: item.metricValues?.[4],
        [columns[4].dataIndex]: item.metricValues?.[1],
        [columns[5].dataIndex]: item.metricValues?.[5],
        [columns[6].dataIndex]: item.metricValues?.[0],
      });
    });
    return formatData;
  }, [columns]);

  const load = async (dimensionName: string, sortParams?: any, pagination?: TablePaginationConfig) => {
    setLoading(true);
    const sorter = sortParams || {
      sortField: 'count',
      sortType: 'DESC',
    };
    const res = await metricService.getPaginationDimensionData({
      productId: getProductId(),
      type: MetricType.TYPE_TRAFFIC_RESOURCE,
      subTypes: [MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION],
      token: analysisToken,
      dimensionNames: [dimensionName],
      ...sorter,
      page: pagination?.current,
      pageSize: pagination?.pageSize,
    });
    setLoading(false);
    setSourceData(res?.data || null);
    setPagination(p => ({
      ...p,
      total: res?.total ?? 0,
    }));
  };

  const handleTableChange = (
    pagination: any,
    _filters: object,
    sorter: any,
    extra: {
      action: 'paginate' | 'sort' | 'filter';
    },
  ) => {
    const sortField = toLineString(columnSortField[sorter.field] ?? '');
    const sortType = getSortType(sorter.order);
    const sortParams = {
      sortField,
      sortType,
    };

    if (extra.action === 'sort' || extra.action === 'paginate') {
      load(dimensionName, sortParams, pagination);
    }

    setPagination(pagination);
  };

  useEffect(() => {
    setPagination(defaultPagination);
    if (analysisToken) {
      load(dimensionName, undefined, defaultPagination);
    }
  }, [analysisToken]);

  return (
    <div className={styles.flow}>
      <div className={styles.flowHeader}>
        <Tabs
          onChange={(dimensionName) => {
            setDimensionName(dimensionName);
            load(dimensionName);
          }}
        >
          {FLOW_OPTIONS.map(item => (
            <Tabs.TabPane tab={item.label} key={item.key} />
          ))}
        </Tabs>
      </div>
      <ProTable
        className={classnames('bugly-table', styles.flowTable)}
        columns={columns}
        search={false}
        toolBarRender={false}
        dataSource={genDimensonData(soruceData)}
        loading={loading}
        onChange={handleTableChange}
        pagination={pagination}
      />
    </div>
  );
};
